// =============================================================================
// io/sync/trades.ts — full-ish trade rows for the TABLE, scoped to the range.
// -----------------------------------------------------------------------------
// The second of the two reads D57 split apart:
//
//   loadTradeMetrics   11 columns, 45-day boot   →  "what are my numbers"
//   loadTradesInRange  the table's columns,      →  "what were these trades"
//                      the ACTIVE date range
//
// The metrics projection cannot render the trades table — `entry`, `exit`,
// `side`, `timeOut`, `maePoints`, `mfePoints`, `grossPnl` and `fees` are simply
// not in it. Widening the narrow read instead would tax every aggregate forever
// to serve rows that are only ever on screen.
//
// This read is cheap because it is SCOPED: the default range is a week.
//
// ── 🚨 THIS MUST NOT HYDRATE ──
//
// `loadFullTrades` exists to make trades EDITABLE: it selects `*` and calls
// `ledger.markHydrated()`, and that ledger entry is what makes a save legal
// (D46). A display read must never do that. Granting write permission for rows a
// user merely scrolled past means a later save can write back a row that was
// never fully read — destroying the columns it lacks, which is precisely the
// failure the ledger exists to prevent.
//
// So there is no ledger parameter here, and there cannot be one added by
// accident: this module does not import the ledger at all.
//
// ── SESSION MODE: PAD THE QUERY, FILTER IN MEMORY ──
//
// The range filter matches on `tradeDayFor`, which in session mode COMPUTES the
// session day from the trade's own date and entry time. Postgres could not be
// asked for that through PostgREST, and `date` is the indexed column — so the
// query is widened by SESSION_PAD_DAYS and narrowed precisely in memory. That is
// the same pattern `bootWindowStart` already uses, for the same reason.
//
// 🔴 IT USED TO READ THE STORED `session_date`, AND THAT WAS THE BUG (TCR-96).
// `selectors/derive.ts` has recomputed that column on every read since the day
// after this file was written, because it is stale or absent on exactly the rows
// that matter — but it only ever saw the rows that SURVIVED this filter. So a
// trade whose column was wrong was dropped a step before anything could correct
// it. Josh lost two 21:26 trades off their own session on 2026-10-25.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { TRADE_COLUMNS, type Trade, type TradeColumnKey } from '../../domain/types';
import { tradeDayFor } from '../../domain/session';
import { dateInBounds, type DayBoundaryMode, type RangeBounds } from '../../domain/range';
import { PAGE_ROWS, SESSION_PAD_DAYS } from './load';
import { PROJECTION_TAINT } from './hydration';

/**
 * What the trades TABLE shows. Transcribed from the old client's
 * `TRADE_COLUMNS_DATAVIEW`, plus the fields its rows are keyed and grouped by.
 *
 * ⚠ `annotated` is here for the "Needs Review" split, not for display.
 * ⚠ `riskPoints` is here for the same reason it is in METRICS_KEYS: it is the
 *   denominator for anything R-denominated, including the excursion columns
 *   under the R display unit. Not a column of its own.
 * ⚠ `sourceAccount` is the Account column's FALLBACK, not a column either. A
 *   trade whose `accountId` resolves to nothing still knows what the broker
 *   export called the account, and showing that beats showing an em dash — it
 *   is the string the user needs in order to assign it.
 * ⚠ `syntheticClose` drives the `syn` chip beside the symbol: the exit was
 *   entered by hand rather than filled. Without it a manually-closed position
 *   is indistinguishable from a real fill.
 * ⚠ `feesAuto` is not shown either. It is the flag that stops
 *   `selectors/derive.ts` overwriting a commission the user typed by hand —
 *   without it, healing the fee would silently discard a manual correction.
 */
export const TABLE_KEYS = [
  'id', 'accountId', 'date', 'sessionDate',
  'timeIn', 'timeOut', 'contract', 'side', 'qty',
  'entry', 'exit', 'points', 'rrRealized', 'riskPoints',
  'maePoints', 'mfePoints',
  'grossPnl', 'fees', 'feesAuto', 'netPnl',
  'annotated', 'sourceAccount', 'syntheticClose',
  // ⚠ NOT a display column either — Setup Quality groups by it, and the global
  // filter's Pattern facet needs it too. One extra column on a read that is
  // already scoped to the active range, versus a second read purely to group by
  // pattern.
  'patternId',
  // ⚠ Same call, for the Exit Model Catalog's per-model stats and for Setup
  // Quality's Pattern × Exit-Model card. Also not displayed in the table.
  'exitModelId',
  // ⚠ Same call again, for the Dashboard's "P&L by Session" card. It is a manual
  // TAG rather than a fact derivable from `timeIn` — the old client stores what
  // the trader chose — so it cannot be computed from a column already read.
  'sessionLabel',
  // ⚠ For the Dashboard's TARGET EFFICIENCY gauge — `rrRealized / rrPlanned`,
  // which is the only metric on the screen that asks whether the trade followed
  // the plan the trader WROTE rather than how it turned out.
  'rrPlanned',
  // ⚠ For the DISCIPLINE SCORE gauge. All six breach flags share ONE jsonb
  // column, so this widens the read by a single field rather than six — worth
  // checking before assuming a flag costs a column.
  //
  // ⚠ AND for Setup Quality's "Hit target" win definition, which reads
  // `targetNotHonored` — a flag that is present on 14 of Josh's 500 trades, so
  // the definition's honesty depends entirely on telling absent from false.
  'booleans',
  // ⚠ For Confluence Edge — which tools confirmed each setup. One jsonb column
  // holding every mark, so this is one field rather than one per confluence.
  // ⚠ MEASURED on the real book before adding it: across all 820 trades the
  // whole column serialises to well under the `executions` annex already on this
  // read, because a mark is `{"active":true}` and the median trade has none.
  'confluences',
  // ⚠ For PEAK CAPTURE. Seconds from entry to the favourable peak — the only
  // thing that places the peak on the fill timeline. Without it the FIFO walk
  // cannot say which contracts were still on when the high printed, so the
  // whole tile reads "no fill timeline" on every row.
  'mfeSecs',
  // ⚠ Its PAIR, for the Excursion tab's Temporal Profile — how fast a setup
  // fails, measured on losers, against how fast it rips, measured on winners.
  // A trade missing EITHER timing is dropped from that card, so shipping one
  // without the other leaves the whole card empty rather than half-answered.
  'maeSecs',
  // 🚨 THE STOP THE POST-EXIT WALK CHARGES BACK, and it was MISSING here while
  // the Coach's Report's own read (`leakUniverse`) carried it. Both surfaces
  // measure the same leak — what holding on would have been worth — and on a
  // head-fake the figure is the distance from the exit back to the stop. Without
  // this column the Excursion tab fell back to `entry ∓ riskPoints`, so the two
  // screens quietly disagreed on every trade whose real stop was not exactly
  // that. One fact, two screens, two answers (D102).
  'plannedStop',
  // 🚨 FOR CUSTOM ANALYTICS' `Avg risk ($)`, AND IT IS NOT DERIVABLE HERE.
  //
  // The obvious move is to skip the column and rebuild the figure as
  // `riskPoints × $/point × qty` — both are already on this read. That is a
  // RECONSTRUCTION, and `domain/leakPlan.riskDollars` already says in writing
  // why the stored figure wins: the two disagree on every scaled position,
  // because the points are the same but the size the trader actually carried
  // is not. A metric named "Avg risk" that quietly reports the risk of a
  // position the trader never held is worse than one that reports nothing.
  //
  // ⚠ ONE NUMERIC COLUMN, on a read already scoped to the active range.
  'riskAtEntry',
  // ── The ANNOTATION facet's three probes. Not displayed either. ──
  //
  // ⚠ They are on the read because the facet lens runs over THESE rows: Trades
  // and the Dashboard both call `applyFacets(ranged.rows, …)`, so a probe absent
  // here reads as "every trade is missing a grade" and the option silently
  // matches the whole book.
  //
  // ⚠ MEASURED, not assumed — across Josh's entire 709-trade book these three
  // columns total **14.9 KB** (notes 10.3, grade 2.7, mistakeTags 1.9; the
  // longest note is 300 characters). This read is scoped to the active range.
  'grade', 'notes', 'mistakeTags',
] as const satisfies readonly TradeColumnKey[];

export type TableKey = (typeof TABLE_KEYS)[number];

/**
 * ── 🚨 `executions` AND `tags` ARE NOT COLUMNS, AND MUST NOT BECOME `Trade` FIELDS ──
 *
 * The per-fill timeline and the applied custom tags both ride the `extras`
 * jsonb. They are read here through PostgREST sub-paths (`extras->executions`,
 * `extras->tags`, aliased) rather than by pulling the whole `extras` blob, so
 * the read stays narrow.
 *
 * ⚠ `tags` differs from `executions` in one way worth stating: it IS a `Trade`
 * field, and `TRADE_COLUMNS.tags` is deliberately `null` for it — the type
 * records "no column" so a projection cannot include it, type-check, and return
 * rows with no tags in them. Reading it here by sub-path is the sanctioned way
 * in; it stays out of `TABLE_KEYS` because `TABLE_KEYS` is what builds the
 * column list.
 *
 * ⚠ It is annexed onto the ROW TYPE and deliberately NOT added to `Trade` /
 * `TRADE_COLUMNS`. A typed `Trade.executions` would oblige `tradeToDb` to
 * serialise it, and the moment it did not, every save would silently drop the
 * fill timeline of the trade being saved — which is D62 exactly: the typed
 * model is lossy, so never own a field you are not going to write back.
 *
 * Rows from this read are tainted `'table'` regardless, so none of them can be
 * written at all. This annex is read-only by construction, not by convention.
 *
 * ⚠ MEASURED BEFORE IT WAS ADDED, on the real book (709 trades): every
 * `executions` array in the entire book serialises to **116 KB**, 3.5 fills per
 * trade, 16 at the widest. The default range is a week. This is not a fat read.
 */
export interface RowExecution {
  time: string | null;
  side: string | null;
  qty: number | null;
  price: number | null;
}

const EXTRAS_ANNEX = [
  'executions:extras->executions',
  'tags:extras->tags',
  // ⚠ For the Excursion tab's Trade Extension and Cost of Premature Exit — how
  // far the trade kept running AFTER the exit. It is written by a metered bar
  // fetch in the old client, so a trade that was never recomputed simply has
  // none, and both cards drop it rather than reading the absence as a zero.
  // Same sub-path read the leak universe already uses for the same field.
  'extensions:extras->extensions',
  // ⚠ WHICH CONTRACT'S TAPE MAE/MFE WAS MEASURED FROM — written by the ATAS
  // logger, never by this client. On a cross-traded book (chart + data on the
  // mini, orders filled as the micro) the excursion is genuinely a NEIGHBOURING
  // contract's, and until this landed nothing said so. Annexed read-only for the
  // same reason as `executions`: a typed `Trade.excursionSource` would oblige
  // `tradeToDb` to write it back, and the day it did not, every save would strip
  // the provenance off the trade being saved — D62.
  // ⚠ Absent is NOT "the traded contract". Absent means the source never said,
  // which is every trade written before 2026-08-14 and every non-logger import.
  'excursionSource:extras->excursionSource',
] as const;

export type TradeTableRow = Pick<Trade, TableKey> & {
  /** Optional so the many row fixtures that predate it still type-check. */
  executions?: readonly RowExecution[] | null;
  /** Custom-tag ids, for the Tag facet. Optional for the same reason. */
  tags?: readonly string[] | null;
  /**
   * The contract whose tape MAE/MFE was measured from, when the source said.
   * ⚠ `null`/absent means UNSTATED, not "the traded contract" — see the annex.
   */
  excursionSource?: string | null;
  /**
   * The stored post-exit runs, keyed by window in minutes (`'30'` is the one
   * both cards read). ⚠ Optional, and absent is NOT zero — see the annex note.
   */
  extensions?: Record<string, Record<string, unknown>> | null;
};

/** Derived, never hand-typed — a literal list drifts on the next migration. */
export const TABLE_COLUMNS: readonly string[] = TABLE_KEYS.map((k) => TRADE_COLUMNS[k]);
export const TABLE_SELECT: string = [...TABLE_COLUMNS, ...EXTRAS_ANNEX].join(',');

const KEY_BY_COLUMN = new Map<string, TableKey>(TABLE_KEYS.map((k) => [TRADE_COLUMNS[k], k]));

export function tableRowFromDb(r: Record<string, unknown>): TradeTableRow {
  const out: Record<string, unknown> = {};
  for (const [col, key] of KEY_BY_COLUMN) {
    // ⚠ `?? null`, not `|| null`. A `false` annotated flag and a 0 P&L are real
    // values, and this row shape is what the Needs Review split reads.
    out[key] = r[col] ?? null;
  }

  // ── 🚨 EVERY ANNEXED FIELD MUST BE COPIED HERE BY HAND ──
  //
  // They arrive under their ALIAS, not under a column name, so the loop above —
  // which walks `KEY_BY_COLUMN` — cannot see them.
  //
  // ⚠ ADDING ONE TO `EXTRAS_ANNEX` IS NOT ENOUGH, and that failure is silent in
  // every direction that normally catches things. `extensions` was added to the
  // select, typed on the row, and `tsc` was clean; PostgREST returned it; and
  // **zero rows carried it** because this function never copied it across. The
  // Excursion tab's premature-exit card read "$0 — no trade has a stored
  // post-exit run" against 316 rows that had one.
  //
  // Nothing mechanical can catch that: the annex list and this block are two
  // places that have to agree, and neither knows about the other. Caught by
  // opening the tab and disbelieving a zero.
  //
  // `Array.isArray` on the first two rather than a cast, because a legacy row
  // can carry an object there — the shape the importer wrote before
  // `assembleImported` was fixed. A non-array must read as "no timeline", never
  // be walked.
  out['executions'] = Array.isArray(r['executions']) ? r['executions'] : null;
  out['tags'] = Array.isArray(r['tags']) ? r['tags'] : null;
  // ⚠ An OBJECT keyed by window in minutes, not an array. `null` when absent,
  // and absent is not "the trade ran nowhere" — both excursion cards that read
  // it drop the trade rather than counting a zero.
  out['extensions'] = r['extensions'] && typeof r['extensions'] === 'object'
    ? r['extensions'] : null;
  // ⚠ A STRING, and `null` means the source never said — NOT "the traded
  // contract". Anything that renders it must show nothing rather than assume,
  // because assuming is exactly the claim this field exists to stop being silent.
  out['excursionSource'] = typeof r['excursionSource'] === 'string' && r['excursionSource']
    ? r['excursionSource'] : null;

  // ── 🚨 TAINTED, and the omission of this was a REAL HOLE ──
  //
  // This row carries 23 of 48 columns. `metricsRowFromDb` marks its projection
  // and this one did not, which left exactly the gap the taint exists to close:
  // the ledger check alone can be defeated, because a projected row carries the
  // row's REAL id — so once a user has OPENED a trade (hydrating it), a table row
  // for that same trade passes `ledger.isComplete` and could be saved, silently
  // destroying the 25 columns it never read (notes, screenshots, tags, mistake
  // tags, discipline booleans, extras).
  //
  // The taint travels with the data instead of with the id, so a spread cannot
  // launder it. `tradeToDb` rejects it before anything is written.
  out[PROJECTION_TAINT] = 'table';

  return out as unknown as TradeTableRow;
}

const shift = (iso: string, days: number): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

export interface LoadTradesResult {
  rows: TradeTableRow[];
  /** Rows the padded query returned before the precise filter — for diagnosis. */
  fetched: number;
  /** Requests it took. A range wide enough to page is worth knowing about. */
  pages: number;
}

/**
 * Rows whose EFFECTIVE date falls inside `bounds`.
 *
 * An unbounded range (`all`) reads everything, which is the user asking for it.
 *
 * ⚠ PAGES TO EXHAUSTION. PostgREST caps a response server-side, so a single
 * request is not "the rows in this range" — it is the first 1,000 of them. The
 * first version of this function issued one request, which is fine for a week
 * and silently wrong for `all` on any real book: the table would show 1,000
 * trades, the KPI strip would total 1,000 trades, and both would agree with each
 * other. `loadTradeMetrics` pages for exactly this reason and the note there
 * spells it out — this read is not exempt just because its default range is
 * narrow, because `all` is one click away.
 */
export async function loadTradesInRange(
  sb: SupabaseClient,
  bounds: RangeBounds,
  mode: DayBoundaryMode,
  sessionTz: string,
): Promise<LoadTradesResult> {
  const out: TradeTableRow[] = [];
  let fetched = 0;
  let pages = 0;

  for (let from = 0; ; from += PAGE_ROWS) {
    let q = sb.from('trades').select(TABLE_SELECT);

    // Pad only when a bound exists. Padding a null bound would invent one.
    if (bounds.startDate) q = q.gte('date', shift(bounds.startDate, -SESSION_PAD_DAYS));
    if (bounds.endDate) q = q.lte('date', shift(bounds.endDate, SESSION_PAD_DAYS));

    // ⚠ ORDERED, and the order is what makes paging correct rather than
    // decorative: `range()` over an unordered query has no defined page
    // boundary, so a concurrent insert shifts rows across it — dropping one and
    // duplicating another with no error. `id` for the same reason as the
    // metrics read: it is stable and unique, which `date` is not.
    const { data, error } = await q
      .order('id', { ascending: true })
      .range(from, from + PAGE_ROWS - 1);
    pages++;

    // Fail LOUD, and on the FIRST bad page. Returning the pages that did arrive
    // would be a short table that looks complete.
    if (error) {
      throw new Error(
        `trades read failed on page ${pages} (rows ${from}–${from + PAGE_ROWS - 1}): ` +
          `${error.message}. Refusing to return a partial range — a short table and a ` +
          `KPI strip totalling the same short set agree with each other and with nothing else.`,
      );
    }
    if (!data || !data.length) break;

    fetched += data.length;
    for (const r of data as unknown as Record<string, unknown>[]) {
      const t = tableRowFromDb(r);
      // The precise filter the padded query could not express. Without it the
      // table shows trades from just outside the range the header claims.
      // 🔴 COMPUTED, NOT READ (TCR-96). Reading the stored `session_date` here
      // dropped a trade out of its own session whenever that column was
      // missing or stale — and `deriveRow`, which corrects it, only ever saw
      // the SURVIVORS of this line. See `tradeDayFor`.
      if (dateInBounds(tradeDayFor(t, mode, sessionTz), bounds)) out.push(t);
    }
    if (data.length < PAGE_ROWS) break; // short page = last page
  }

  return { rows: out, fetched, pages };
}

/**
 * The FACET UNIVERSE — a tiny projection of the whole book.
 *
 * ⚠ Deliberately NOT date-scoped. The old client counts facet options over the
 * account-scoped set across ALL TIME, "so options stay stable as you narrow
 * (they don't vanish when the view is empty)". Counting from the visible range
 * makes options appear and disappear as the date filter moves — the behaviour
 * that change was made to fix.
 *
 * Four columns, read once per session. Cheap enough to be honest with: a book of
 * 671 trades is a single page.
 */
export const UNIVERSE_KEYS = [
  'id', 'accountId', 'contract', 'date', 'sessionDate',
  // ⚠ For the Pattern facet's per-option COUNTS. Without it the drawer can list
  // the catalogue but every count reads 0, which looks like "you have never
  // traded any of your own setups" — a worse screen than no counts at all.
  'patternId',
  // ⚠ For the ANNOTATION facet's counts, and they are the whole point of that
  // dimension: "Missing grade: 214" is the number the user opened the panel for.
  // Every count reading 0 would say the review queue is empty when it is not.
  //
  // ⚠ MEASURED before they were added, on the real book: all three across all
  // 709 trades total **14.9 KB** (notes 10.3, grade 2.7, mistakeTags 1.9). This
  // read is the whole book by design, so the measurement is the whole cost.
  'grade', 'notes', 'mistakeTags',
] as const satisfies readonly TradeColumnKey[];

/**
 * ⚠ `tags` is annexed, not keyed — it rides `extras` and `TRADE_COLUMNS.tags` is
 * `null`, so it cannot go in `UNIVERSE_KEYS` (which builds the column list).
 * Same sub-path read as the table's, and 1.4 KB across the whole book.
 */
const UNIVERSE_ANNEX = ['tags:extras->tags'] as const;

export type UniverseRow = Pick<Trade, (typeof UNIVERSE_KEYS)[number]> & {
  tags?: readonly string[] | null;
};

const UNIVERSE_SELECT = [
  ...UNIVERSE_KEYS.map((k) => TRADE_COLUMNS[k]),
  ...UNIVERSE_ANNEX,
].join(',');

const UNIVERSE_KEY_BY_COLUMN = new Map<string, (typeof UNIVERSE_KEYS)[number]>(
  UNIVERSE_KEYS.map((k) => [TRADE_COLUMNS[k] as string, k]),
);

export async function loadFacetUniverse(sb: SupabaseClient): Promise<UniverseRow[]> {
  const out: UniverseRow[] = [];

  for (let from = 0; ; from += PAGE_ROWS) {
    const { data, error } = await sb
      .from('trades')
      .select(UNIVERSE_SELECT)
      .order('id', { ascending: true })
      .range(from, from + PAGE_ROWS - 1);

    // ⚠ Throws rather than returning a short list. A truncated universe silently
    // drops instruments out of the filter panel, and an option that is not
    // offered cannot be noticed as missing.
    if (error) throw new Error(`facet universe read failed on page ${(from / PAGE_ROWS) + 1}: ${error.message}`);
    if (!data || !data.length) break;

    for (const r of data as unknown as Record<string, unknown>[]) {
      // ⚠ DERIVED from the column map, never hand-typed. The literal version of
      // this loop is where a real defect came from once: `account_id` was
      // selected unaliased and read back as `accountId`, so every row carried
      // null and the screen rendered an em dash everywhere. A map keyed by the
      // same constant that built the select cannot disagree with it.
      const row: Record<string, unknown> = {};
      for (const [col, key] of UNIVERSE_KEY_BY_COLUMN) row[key] = r[col] ?? null;
      // Under its ALIAS, and array-checked — a legacy row can carry an object.
      row['tags'] = Array.isArray(r['tags']) ? r['tags'] : null;
      out.push(row as unknown as UniverseRow);
    }
    if (data.length < PAGE_ROWS) break;
  }

  return out;
}

/**
 * EVERY TRADE ID, AND NOTHING ELSE — the poll probe (TCR-95).
 *
 * Beta's `syncNewTrades` (`app/index.html:4071`) selects only the `id` column,
 * subtracts the ids it already holds and fetches full rows for the missing ones
 * alone. This is that first half. The second half is not needed here: beta has
 * one global `state.trades` to merge into, and this client has eleven tabs each
 * reading their own window — so the caller bumps a shared nonce and every
 * mounted `useTradeRows` re-reads the range it actually wants.
 *
 * ⚠ IT RETURNS IDS, NOT ROWS, and the distinction is the whole cost argument.
 * `TABLE_SELECT` is 25 columns; this is one. On Josh's book (~700 trades) a
 * page of ids is a few tens of KB, which is why beta can afford to do it every
 * 15 seconds and has done for months.
 *
 * ⚠ NO DATE BOUND, on purpose. A trade pushed from ATAS can land on any date —
 * a replay session is dated whenever the replay was — and a probe scoped to the
 * visible range would not see it.
 *
 * ⚠ PAGED, like every other read here. An unpaged probe on a book over
 * `PAGE_ROWS` would return a short set, which compares as "ids disappeared" and
 * would fire a re-read on every single cycle.
 */
export async function loadTradeIds(sb: SupabaseClient): Promise<ReadonlySet<string>> {
  const out = new Set<string>();

  for (let from = 0; ; from += PAGE_ROWS) {
    const { data, error } = await sb
      .from('trades')
      .select('id')
      .order('id', { ascending: true })
      .range(from, from + PAGE_ROWS - 1);

    // ⚠ THROWS rather than returning what arrived. A short page read as the
    // whole book is a set that differs from the last one, so a silently
    // truncated probe would bump the nonce every cycle — eleven tabs
    // re-reading their full range every 15 seconds, caused by the very thing
    // meant to stop them.
    if (error) {
      throw new Error(
        `trade id probe failed on page ${(from / PAGE_ROWS) + 1}: ${error.message}`,
      );
    }
    if (!data || !data.length) break;

    for (const r of data as unknown as Record<string, unknown>[]) out.add(String(r['id']));
    if (data.length < PAGE_ROWS) break; // short page = last page
  }

  return out;
}
